import { useEffect, useState } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody,
  Checkbox, TextField, Button, Typography
} from '@mui/material';
import axiosInstance from '../../helpers/Axios';

interface IPage {
  name: string;
  route: string;
}

interface IRight {
  user: string;
  route: string;
}

interface IAccessData {
  pages: IPage[];
  users: string[];
  rights: IRight[];
}

const emptyData: IAccessData = { pages: [], users: [], rights: [] };

const Droits = () => {
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const [newPageName, setNewPageName] = useState('');
  const [newPageRoute, setNewPageRoute] = useState('');
  const [newUser, setNewUser] = useState('');

  const load = () => {
    axiosInstance.get('/adminaccess')
      .then(response => setData(response.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const save = (next: IAccessData) => {
    setData(next);
    axiosInstance.put('/adminaccess', next);
  };

  const hasRight = (user: string, route: string) =>
    data.rights.some(r => r.user === user && r.route === route);

  const toggleRight = (user: string, route: string) => {
    const rights = hasRight(user, route)
      ? data.rights.filter(r => !(r.user === user && r.route === route))
      : [...data.rights, { user, route }];
    save({ ...data, rights });
  };

  const addPage = () => {
    if (!newPageName.trim() || !newPageRoute.trim())
      return;
    save({ ...data, pages: [...data.pages, { name: newPageName.trim(), route: newPageRoute.trim() }] });
    setNewPageName('');
    setNewPageRoute('');
  };

  const addUser = () => {
    const trimmed = newUser.trim();
    if (!trimmed || data.users.includes(trimmed))
      return;
    save({ ...data, users: [...data.users, trimmed] });
    setNewUser('');
  };

  if (loading)
    return <p>Chargement en cours...</p>;

  return (
    <div id="courses">
      <div id="title">
        <h1>Gestion des droits admin</h1>
      </div>
      <div id="content">
        <Table sx={{ width: '100%', mb: 4 }}>
          <TableHead>
            <TableRow>
              <TableCell>Page</TableCell>
              {data.users.map(u => <TableCell key={u} align="center">{u}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.pages.map(p => (
              <TableRow key={p.route}>
                <TableCell>
                  {p.name}{' '}
                  <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.8em' }}>
                    ({p.route})
                  </Typography>
                </TableCell>
                {data.users.map(u => (
                  <TableCell key={u} align="center">
                    <Checkbox checked={hasRight(u, p.route)} onChange={() => toggleRight(u, p.route)} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
          <TextField label="Nom de la page" size="small" value={newPageName} onChange={e => setNewPageName(e.target.value)} />
          <TextField label="Route (ex: /admin-xxx)" size="small" value={newPageRoute} onChange={e => setNewPageRoute(e.target.value)} />
          <Button variant="outlined" onClick={addPage}>Ajouter une page</Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField label="Nouvel utilisateur" size="small" value={newUser} onChange={e => setNewUser(e.target.value)} />
          <Button variant="outlined" onClick={addUser}>Ajouter un utilisateur</Button>
        </Box>

        <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: '0.9em' }}>
          Pour qu'un nouvel utilisateur ait sa propre page (ex: /pascal), il faut encore créer son composant
          dans le code (voir src/pages/Users/Jay.tsx comme exemple) et ajouter la route correspondante,
          en plus de l'ajouter ici.
        </Typography>
      </div>
    </div>
  )
};

export default Droits;
